import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Menu, MenuItem, Tooltip, Typography, Divider } from '@mui/material';
import './user-status.css';
import { AccountCircleSharp } from '@material-ui/icons';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  isAuthenticated, updateProfile, getUserIndicators, getUsers,
  updateUserPresence, getUser, selectUserStatus, setUserStatus, updateChatStatus
} from '../../redux/user/user.actions';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { getServiceGroups } from '../../redux/serviceGroup/serviceGroup.actions';
import { getProfiles } from '../../redux/profile/profile.actions';
import { useWebSocket } from '../../components/WebSocketContext/WebSocketContext';

const PRESENCE_PAUSE_PERMISSION = '#pausar-status-online-operadores';

const UserStatus = ({ userId, isIcon, ...props }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { updateUserStatus } = useWebSocket();
  const userStatus = useSelector(selectUserStatus);
  const dispatch = useDispatch();

  const { currentUser, permissions, updateUserPresence: setPresenceRemote } = props;

  const canChangePresence = useMemo(() => {
    const list = Array.isArray(permissions) ? permissions : [];
    return (
      list.includes(PRESENCE_PAUSE_PERMISSION) ||
      Number(currentUser?.profile_id) === 1 ||
      Boolean(currentUser?.is_super_admin)
    );
  }, [permissions, currentUser?.profile_id, currentUser?.is_super_admin]);

  const history = useHistory();

  useEffect(() => {
    if (!isAuthenticated()) {
      history.push({
        pathname: '/login',
        state: { operators: currentUser }
      });
    }
  }, [props, history, currentUser]);

  // Abre sempre: o status de SISTEMA continua gated por permissão (itens do menu
  // desabilitados), mas o status de CHAT o próprio operador sempre pode alterar.
  const handleClick = (event) => {
    event.preventDefault()
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (status) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canChangePresence) return;
    dispatch(setUserStatus(status));
    setPresenceRemote(status.userId, status.status);
    handleClose();
    updateUserStatus({ userId: status.userId, status: status.status });
  };

  // Status de chat (0 Indisponível / 1 Disponível) do próprio operador.
  const chatStatus = Number(currentUser?.chat_status) || 0;
  const handleChatStatusChange = (value) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(updateChatStatus(currentUser?.id, value));
    handleClose();
  };

  useEffect(() => {
    dispatch(setUserStatus({ userId: currentUser?.id, status: currentUser?.status }));
  }, [currentUser, dispatch])

  // Auto-away por inatividade foi REMOVIDO (decisão do item 11): o status de
  // sistema agora só muda manualmente (menu) ou no login/logout. Se voltarmos a
  // querer "ausente automático" no futuro, reintroduzir um timer aqui.

  const getStatusText = (status) => {
    if (!canChangePresence) {
      switch (status) {
        case 1:
        case 2: return 'Ativo';
        case 3: return 'Inativo';
        default: return 'Status desconhecido';
      }
    }
    switch (status) {
      case 1: return 'Ativo - Clique para alterar status';
      case 2: return 'Ativo - Clique para alterar status';
      case 3: return 'Inativo - Clique para alterar status';
      default: return 'Status desconhecido';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 1: return 'online';
      case 2: return 'online';
      case 3: return 'offline';
      default: return '';
    }
  };

  const renderMenuItems = () => {
    const sysOnline = userStatus?.status === 1 || userStatus?.status === 2;
    return (
      <>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 700, color: 'text.secondary' }}>
          Status no sistema
        </Typography>
        <MenuItem disabled={!canChangePresence} onClick={handleStatusChange({ userId: currentUser?.id, status: 1 })}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50' }} />
            Ativo{sysOnline ? ' · atual' : ''}
          </Box>
        </MenuItem>
        <MenuItem disabled={!canChangePresence} onClick={handleStatusChange({ userId: currentUser?.id, status: 3 })}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#F44336' }} />
            Inativo{!sysOnline ? ' · atual' : ''}
          </Box>
        </MenuItem>
        <Divider />
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 700, color: 'text.secondary' }}>
          Status no chat
        </Typography>
        <MenuItem onClick={handleChatStatusChange(1)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50' }} />
            Disponível{chatStatus === 1 ? ' · atual' : ''}
          </Box>
        </MenuItem>
        <MenuItem onClick={handleChatStatusChange(0)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9e9e9e' }} />
            Indisponível{chatStatus === 0 ? ' · atual' : ''}
          </Box>
        </MenuItem>
      </>
    );
  };

  return (
    <Box>
      {isIcon ? (
        <Box className="profile-icon" sx={{ position: 'relative', display: 'flex', alignItems: 'center', '& .icon': { color: 'white !important' }, '& svg': { color: 'white !important' } }}>
          <AccountCircleSharp fontSize="medium" className='icon' sx={{ color: 'white !important', fill: 'white !important' }} />
          <Tooltip 
            title={getStatusText(userStatus?.status)} 
            arrow 
            placement="right"
          >
            <Box 
              className={`status-icon ${getStatusClass(userStatus?.status)}`}
              onClick={handleClick}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.1) translateY(-50%)',
                  transition: 'transform 0.2s ease'
                }
              }}
            />
          </Tooltip>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {renderMenuItems()}
          </Menu>
        </Box>
      ) : (
        <Grid sx={{ backgroundColor: 'transparent', cursor: 'pointer' }}>
          <Grid container onClick={handleClick}>
            <Box>
              {currentUser?.status === 1 ? 'Ativo'
                : currentUser?.status === 2 ? 'Ativo'
                : currentUser?.status === 3 ? 'Inativo' : ''}
            </Box>
            <Box className={`page-status ${getStatusClass(userStatus?.status)}`} />
          </Grid>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {renderMenuItems()}
          </Menu>
        </Grid>
      )}
    </Box>
  );
};

const mapStateToProps = state => {
  const { user } = state;
  const { indicatorUser, currentUser, permissions } = user;
  return { indicatorUser, user, currentUser, permissions };
};

const mapDispatchToProps = dispatch => ({
  getUserIndicators: () => dispatch(getUserIndicators()),
  updateProfile: (profile) => dispatch(updateProfile(profile)),
  updateUserPresence: (userId, status) => dispatch(updateUserPresence(userId, status)),
  getUsers: () => dispatch(getUsers()),
  getServiceGroups: () => dispatch(getServiceGroups()),
  getUser: (id) => dispatch(getUser(id)),
  getProfiles: () => dispatch(getProfiles()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserStatus);